import { useSyncExternalStore } from 'react';

/**
 * #1429 — Cold-load connection-budget gate.
 *
 * Returns `false` on first render, then flips to `true` shortly after the app
 * has painted (browser idle callback, with a ~1.5s hard-cap fallback). Gate
 * NON-critical background queries (notification count, badges, banners,
 * shared dashboards, org switcher, AI status) on this so they do not contend
 * with the critical `/bootstrap` + page-data requests for the browser's ~6
 * connections-per-origin on a cold load.
 *
 * It is a module-level singleton: the flag flips once per page session and
 * stays `true`, so client-side route changes are unaffected (no repeated
 * deferral). In a test environment it starts `true` so suites don't have to
 * advance timers to see these queries run.
 */

const IS_TEST =
  typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';

let ready = IS_TEST;
let scheduled = IS_TEST;
const listeners = new Set<() => void>();

function flip(): void {
  if (ready) return;
  ready = true;
  listeners.forEach((l) => l());
}

function schedule(): void {
  if (scheduled) return;
  scheduled = true;
  const ric = (typeof window !== 'undefined'
    ? (window as unknown as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
      }).requestIdleCallback
    : undefined);
  if (ric) {
    ric(() => flip(), { timeout: 1500 });
  } else {
    setTimeout(flip, 1200);
  }
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  schedule();
  return () => {
    listeners.delete(cb);
  };
}

export function useDeferredReady(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => ready,
    () => ready,
  );
}
